// client/src/dnd/emitter_tool/components/stages/GridCanvas.js
import React, { useCallback, useEffect, useRef } from "react";
import { clamp01 } from "../../utils/clamp";

export default function GridCanvas({ show, pxPerMeter }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const configRef = useRef({
    cell_cols: 16,
    start_x: 0, start_y: 0,
    offset_x: 0, offset_y: 0,
    color: "#ffffff", opacity: 0.25,
  });

  const toRGBA = (hex, a) => {
    const h = (hex || "#ffffff").replace("#", "");
    const expand = (s) => (s.length === 3 ? s.split("").map((c) => c + c).join("") : s);
    const bigint = parseInt(expand(h), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    const alpha = clamp01(Number(a));
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const draw = useCallback(() => {
    const wrap = wrapRef.current, cvs = canvasRef.current;
    if (!wrap || !cvs) return;

    const ctx = cvs.getContext("2d");
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cvs.width, cvs.height);

    if (!show || !pxPerMeter || pxPerMeter <= 0) return;

    const { start_x = 0, start_y = 0, offset_x = 0, offset_y = 0, color, opacity } = configRef.current;
    const step = pxPerMeter * dpr;
    const minor = step / 2;

    const baseOffsetX = (offset_x * dpr) + (start_x * step);
    const baseOffsetY = (offset_y * dpr) + (start_y * step);

    const firstMajorX = ((baseOffsetX % step) + step) % step;
    const firstMajorY = ((baseOffsetY % step) + step) % step;
    const firstMinorX = ((baseOffsetX % minor) + minor) % minor;
    const firstMinorY = ((baseOffsetY % minor) + minor) % minor;

    ctx.beginPath();
    ctx.lineWidth = Math.max(1, 1 * dpr);
    ctx.strokeStyle = toRGBA(color, Math.min(1, Number(opacity) * 0.6));
    for (let x = firstMinorX; x <= cvs.width; x += minor) { ctx.moveTo(Math.floor(x)+0.5, 0); ctx.lineTo(Math.floor(x)+0.5, cvs.height); }
    for (let y = firstMinorY; y <= cvs.height; y += minor) { ctx.moveTo(0, Math.floor(y)+0.5); ctx.lineTo(cvs.width, Math.floor(y)+0.5); }
    ctx.stroke();

    ctx.beginPath();
    ctx.lineWidth = Math.max(1, 1.5 * dpr);
    ctx.strokeStyle = toRGBA(color, opacity);
    for (let x = firstMajorX; x <= cvs.width; x += step) { ctx.moveTo(Math.floor(x)+0.5, 0); ctx.lineTo(Math.floor(x)+0.5, cvs.height); }
    for (let y = firstMajorY; y <= cvs.height; y += step) { ctx.moveTo(0, Math.floor(y)+0.5); ctx.lineTo(cvs.width, Math.floor(y)+0.5); }
    ctx.stroke();
  }, [show, pxPerMeter]);

  useEffect(() => {
    const wrap = wrapRef.current, cvs = canvasRef.current;
    if (!wrap || !cvs) return;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const W = Math.max(1, Math.floor(rect.width * dpr));
      const H = Math.max(1, Math.floor(rect.height * dpr));
      if (cvs.width !== W || cvs.height !== H) {
        cvs.width = W; cvs.height = H;
        cvs.style.width = `${Math.floor(rect.width)}px`;
        cvs.style.height = `${Math.floor(rect.height)}px`;
      }
      draw();
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    window.addEventListener("resize", resize);
    resize();

    return () => { ro.disconnect(); window.removeEventListener("resize", resize); };
  }, [draw]);

  useEffect(() => { draw(); }, [draw, show, pxPerMeter]);

  useEffect(() => {
    const onCfg = (e) => {
      const cfg = e?.detail?.config;
      if (!cfg || typeof cfg !== "object") return;
      configRef.current = { ...configRef.current, ...cfg };
      draw();
    };
    window.addEventListener("EmitterTool:UI:GridConfigChange", onCfg);
    try { window.dispatchEvent(new CustomEvent("EmitterTool:UI:RequestGridConfig")); } catch {}
    return () => window.removeEventListener("EmitterTool:UI:GridConfigChange", onCfg);
  }, [draw]);

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0 }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block", pointerEvents: "none" }} />
    </div>
  );
}
